import { capitalizeFirstLetter } from './utilities/stringUtils.js';
import { format, setHours, setMinutes } from 'date-fns';
import { MIN_TIME_PERIOD_HOURS, MIN_HOURS, MAX_TIME_PERIOD_HOURS, MAX_HOURS, AM_TIME, AFTER, BEFORE, PM_TIME, FULL_TIME_REGEX_MERIDIUM, VALID_12_HOURS_DISPLAY_VALUE_LENGTH, FULL_TIME_REGEX_24, VALID_24_HOURS_DISPLAY_VALUE_LENGTH, FULL_TIME_REGEX_24_WITH_SECONDS, HELPER_TEXT_MERIDIEM, HELPER_TEXT_24_HOURS } from './TimePicker/constants.js';

let RELATIVE_FIELD_POSITION = /*#__PURE__*/function (RELATIVE_FIELD_POSITION) {
  RELATIVE_FIELD_POSITION["LEFT"] = "left";
  RELATIVE_FIELD_POSITION["RIGHT"] = "right";
  return RELATIVE_FIELD_POSITION;
}({});

const getHoursLimitValues = useMeridiem => {
  return {
    minHoursValue: useMeridiem ? MIN_TIME_PERIOD_HOURS : MIN_HOURS,
    maxHoursValue: useMeridiem ? MAX_TIME_PERIOD_HOURS : MAX_HOURS
  };
};
const getTimeObject = (time, useMeridiem) => {
  if (!time) return {
    hours: 0,
    minutes: 0,
    meridiem: AM_TIME,
    relativeModeValue: AFTER
  };
  const splitTime = time.split(':');
  const parsedHours = parseInt(splitTime[0], 10);
  const parsedMinutes = parseInt(splitTime[1], 10);
  const hours = isNaN(parsedHours) ? null : getHoursByTimeFormat(parsedHours, useMeridiem);
  const minutes = isNaN(parsedMinutes) ? null : parsedMinutes;
  const meridiem = getMeridiemValue(parsedHours);
  const relativeModeValue = splitTime[0][0] === '-' ? BEFORE : AFTER;
  return {
    hours: Math.abs(hours),
    minutes,
    meridiem,
    relativeModeValue
  };
};
const getHoursByTimeFormat = (hours, useMeridiem) => {
  if (!useMeridiem) return hours;
  if (hours > 12) {
    return hours - 12;
  }
  if (hours === 0) return 12;
  return hours;
};
const getMeridiemValue = hours => {
  if (hours === null) return null;
  return hours >= 12 ? PM_TIME : AM_TIME;
};
const getTimeString = ({
  hours,
  minutes,
  meridiem,
  useMeridiem,
  relativeMode
}) => {
  const baseValue = `:${minutes < 10 ? '0' : ''}${minutes}:00`;
  let resultHours = hours;
  if (useMeridiem) {
    if (meridiem === AM_TIME) {
      resultHours = hours === 12 ? 0 : hours;
    } else {
      resultHours = hours === 12 ? hours : hours + 12;
    }
  }
  const resultRelativeMode = relativeMode === BEFORE ? '-' : '';
  return `${resultRelativeMode}${resultHours < 10 ? '0' : ''}${resultHours}${baseValue}`;
};
const getMeridiemOptions = () => {
  return [{
    value: AM_TIME,
    label: AM_TIME
  }, {
    value: PM_TIME,
    label: PM_TIME
  }];
};
const getRelativeModeOptions = labels => {
  return [{
    value: BEFORE,
    label: labels.before
  }, {
    value: AFTER,
    label: labels.after
  }];
};
const getRelativeModeLabel = (mode, labels) => {
  return getRelativeModeOptions(labels).find(item => item.value === mode).label;
};
const getDropdownValue = ({
  isRelativeMode,
  timePickerValue,
  timeFormat,
  labels,
  relativeFieldPosition
}) => {
  if (timePickerValue === null) return null;
  if (isRelativeMode) {
    const isLeftPosition = relativeFieldPosition === RELATIVE_FIELD_POSITION.LEFT;
    const time = formatTime(timePickerValue.replace(/-/g, ''), timeFormat);
    const relativeModeLabel = capitalizeFirstLetter(timePickerValue[0] === '-' ? getRelativeModeLabel(BEFORE, labels) : getRelativeModeLabel(AFTER, labels));
    return isLeftPosition ? `${relativeModeLabel} ${time}` : `${time} ${relativeModeLabel}`;
  }
  return formatTime(timePickerValue, timeFormat);
};
const formatTime = (time, timeFormat) => {
  const today = new Date();
  const [hours, minutes] = time.split(':');
  const date = setHours(setMinutes(today, parseInt(minutes, 10)), parseInt(hours, 10));
  return format(date, timeFormat);
};
const isTimeDisplayValueValid = (value, useMeridiem = false) => {
  if (value === null) return true;
  return useMeridiem ? new RegExp(FULL_TIME_REGEX_MERIDIUM).test(value) && value.length === VALID_12_HOURS_DISPLAY_VALUE_LENGTH : new RegExp(FULL_TIME_REGEX_24).test(value) && value.length === VALID_24_HOURS_DISPLAY_VALUE_LENGTH;
};
const isTimeValid = value => {
  return new RegExp(FULL_TIME_REGEX_24_WITH_SECONDS).test(value === null || value === void 0 ? void 0 : value.replace('-', ''));
};
const getHelperMaskText = useMeridiem => useMeridiem ? HELPER_TEXT_MERIDIEM : HELPER_TEXT_24_HOURS;
const calculateNewTimeValue = (value, useMeridiem, relativeMode) => {
  if (!value) return null;
  const [hours, minutes] = value.slice(0, 5).split(':');
  const meridiem = value.slice(-2).toUpperCase();
  return getTimeString({
    hours: parseInt(hours, 10),
    minutes: parseInt(minutes, 10),
    meridiem,
    useMeridiem,
    relativeMode
  });
};
const getInputErrorConfig = (value, isValueValid, labels) => isValueValid || value === null ? undefined : {
  errorMessage: labels.invalidTimeFormat
};
const isHelperTextVisible = (isInputFocused, displayValue) => {
  return isInputFocused && !displayValue;
};

var utils = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getHoursLimitValues: getHoursLimitValues,
  getTimeObject: getTimeObject,
  getHoursByTimeFormat: getHoursByTimeFormat,
  getMeridiemValue: getMeridiemValue,
  getTimeString: getTimeString,
  getMeridiemOptions: getMeridiemOptions,
  getRelativeModeOptions: getRelativeModeOptions,
  getRelativeModeLabel: getRelativeModeLabel,
  getDropdownValue: getDropdownValue,
  formatTime: formatTime,
  isTimeDisplayValueValid: isTimeDisplayValueValid,
  isTimeValid: isTimeValid,
  getHelperMaskText: getHelperMaskText,
  calculateNewTimeValue: calculateNewTimeValue,
  getInputErrorConfig: getInputErrorConfig,
  isHelperTextVisible: isHelperTextVisible
});

export { RELATIVE_FIELD_POSITION as R, getDropdownValue as a, getInputErrorConfig as b, calculateNewTimeValue as c, isHelperTextVisible as d, getHelperMaskText as e, isTimeDisplayValueValid as f, getTimeObject as g, getTimeString as h, isTimeValid as i, getRelativeModeOptions as j, getHoursLimitValues as k, getMeridiemOptions as l, getHoursByTimeFormat as m, getMeridiemValue as n, getRelativeModeLabel as o, formatTime as p, utils as u };
